import { BellIcon, BellSlashIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import type { ClubTheme } from './themes';
import type { NotificationLevel } from './types';
import { useSubscriptions, useUpdateSubscription, useMarkChannelRead } from './queries';

interface SubscriptionSettingsProps {
  clubId: string;
  theme: ClubTheme;
}

const NOTIFICATION_OPTIONS: { value: NotificationLevel; label: string; desc: string }[] = [
  { value: 'everything', label: 'Everything', desc: 'All posts, events, replies' },
  { value: 'highlights', label: 'Highlights', desc: 'Events + posts with 3+ reactions' },
  { value: 'events_only', label: 'Events only', desc: 'Just event announcements' },
  { value: 'mentions_only', label: 'Mentions', desc: 'Only when @tagged' },
  { value: 'muted', label: 'Muted', desc: 'Nothing, check manually' },
];

export default function SubscriptionSettings({ clubId, theme }: SubscriptionSettingsProps) {
  const { data: subscriptions, isLoading } = useSubscriptions(clubId);
  const updateSub = useUpdateSubscription(clubId);
  const markRead = useMarkChannelRead(clubId);

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 animate-pulse rounded-lg" style={{ backgroundColor: `${theme.colors.muted}10` }} />
        ))}
      </div>
    );
  }

  if (!subscriptions?.length) {
    return (
      <p className="p-4 text-center text-xs" style={{ color: theme.colors.muted }}>
        No channel subscriptions yet.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {subscriptions.map((sub) => (
        <div
          key={sub.id}
          className="group flex items-center gap-2 rounded-xl px-3 py-2 transition-colors hover:bg-opacity-10"
          style={{ backgroundColor: sub.pinned ? `${theme.colors.primary}08` : 'transparent' }}
        >
          {/* Pin toggle */}
          <button
            type="button"
            onClick={() =>
              updateSub.mutate({ channelId: sub.channel_id, pinned: !sub.pinned })
            }
            className="shrink-0"
            title={sub.pinned ? 'Unpin channel' : 'Pin channel'}
          >
            {sub.pinned ? (
              <StarSolidIcon className="h-4 w-4" style={{ color: theme.colors.primary }} />
            ) : (
              <StarIcon
                className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-60"
                style={{ color: theme.colors.muted }}
              />
            )}
          </button>

          {/* Channel name + unread */}
          <div className="flex flex-1 items-center gap-2 min-w-0">
            <span className="text-xs opacity-50" style={{ color: theme.colors.muted }}>#</span>
            <span className="truncate text-sm" style={{ color: theme.colors.text }}>
              {sub.channel?.name}
            </span>
            {sub.unread_count > 0 && (
              <button
                type="button"
                onClick={() => markRead.mutate(sub.channel_id)}
                className="shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
                title="Mark as read"
              >
                {sub.unread_count > 99 ? '99+' : sub.unread_count}
              </button>
            )}
          </div>

          {/* Notification level picker */}
          <select
            value={sub.notification_level}
            onChange={(e) =>
              updateSub.mutate({
                channelId: sub.channel_id,
                notification_level: e.target.value as NotificationLevel,
              })
            }
            className="shrink-0 rounded-lg border-none bg-transparent px-1 py-0.5 text-[11px] outline-none"
            style={{ color: theme.colors.muted }}
            title="Notification level"
          >
            {NOTIFICATION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* Muted indicator */}
          {sub.notification_level === 'muted' ? (
            <BellSlashIcon className="h-3.5 w-3.5 shrink-0" style={{ color: theme.colors.muted }} />
          ) : (
            <BellIcon className="h-3.5 w-3.5 shrink-0 opacity-30" style={{ color: theme.colors.muted }} />
          )}
        </div>
      ))}
    </div>
  );
}
